import React from 'react';
import ServiceAttributeSummary from './ServiceAttributeSummary';
import { ServiceAttributeSummaryData } from '../../models/PulseData';
import { FetchState } from '../../models/commonTypes';
import PulseContentHeader from './PulseContentHeader';
import PulseContentFooter from './PulseContentFooter';
import { copyToClipboard, copyImageToClipboard } from '../../utils/clipboardUtils';
import { useNotifier } from '../../hooks/useNotifier';
import { toBlob } from 'html-to-image';
import logger from '../../utils/logger';
import { openPrintView } from './printUtils';
import { SummaryType } from './PulseNav';
import { GroupAttributeViewRenderPayload } from './PulseContent';

export const GROUP_ATTRIBUTE_VIEW_CONTENT_ID = 'pulse-group-attribute-summary-section';

interface GroupAttributeViewProps extends GroupAttributeViewRenderPayload {
  // Common props
  servicesGroup: string;
  currentStartDate: Date;
  currentEndDate: Date;
  mainCssUrl: string;
  isLoadingOverall: boolean; // Or use groupAttributeSummaryState.isLoading
  activeSummary: SummaryType;
}

const GroupAttributeView: React.FC<GroupAttributeViewProps> = ({
  servicesGroup,
  selectedGroupAttribute,
  groupAttributeSummaryState,
  // Common props
  currentStartDate,
  currentEndDate,
  mainCssUrl,
  // isLoadingOverall, // Using specific isLoading below
  // activeSummary, // selectedGroupAttribute is more specific
}) => {
  const groupAttributeSummaryData = groupAttributeSummaryState.data;
  const { showNotification } = useNotifier();

  const handleCopyRawData = async () => {
    if (!groupAttributeSummaryData) { showNotification('No data to copy.', 'warning'); return; }
    const payload = getGroupAttributeRawDataPayload(groupAttributeSummaryData, selectedGroupAttribute);
    if (payload) await copyToClipboard(JSON.stringify(payload.data, null, 2), showNotification, payload.message);
  };

  const handleCopyView = async () => {
    const viewCopyInfo = getGroupAttributeViewCopyInfo(selectedGroupAttribute);
    const elementToCapture = document.getElementById(viewCopyInfo.elementId);
    if (elementToCapture) {
      try {
        const imageBlob = await toBlob(elementToCapture, { backgroundColor: 'white' });
        if (imageBlob) {
          await copyImageToClipboard(imageBlob, showNotification, viewCopyInfo.successMessage);
        } else {
          throw new Error('Failed to generate blob from element.');
        }
      } catch (err) {
        logger.error(`Error capturing group attribute view for ${selectedGroupAttribute}:`, err);
        showNotification('Failed to copy group attribute view as image. See console for details.', 'error');
      }
    } else {
      showNotification(`Element with ID '${viewCopyInfo.elementId}' not found for copying view.`, 'warning');
    }
  };

  const handlePrintView = async () => {
    const printInfo = getGroupAttributePrintMetadata(servicesGroup, selectedGroupAttribute);
    const elementToCapture = document.getElementById(printInfo.elementId);
    if (elementToCapture) {
      openPrintView({ elementToCapture, jobTitle: printInfo.jobTitle, mainCssUrl, headerContent: printInfo.headerContent, showNotification });
    } else {
      showNotification('Could not find group attribute content to print.', 'warning');
    }
  };

  const title = `Key Attribute: ${selectedGroupAttribute}`;

  return (
    <>
      <PulseContentHeader title={title} isLoading={groupAttributeSummaryState.isLoading} onCopyRawData={handleCopyRawData} onCopyView={handleCopyView} onPrintView={handlePrintView} />
      <div id={GROUP_ATTRIBUTE_VIEW_CONTENT_ID}>
      <h3 className="text-xl font-semibold mb-3 text-text-primary">
        Summary for Attribute: <span className="text-accent-primary">{selectedGroupAttribute}</span>
        {servicesGroup && servicesGroup !== "All" && (
          <>
            {' '}in Services Group: <span className="text-accent-primary">{servicesGroup}</span>
          </>
        )}
      </h3>

      {groupAttributeSummaryState.error && (
        <div className="error-banner">
          <p>Error loading summary for attribute {selectedGroupAttribute}: {groupAttributeSummaryState.error}</p>
        </div>
      )}

      {groupAttributeSummaryState.isLoading ? (
        <p>Loading summary for attribute {selectedGroupAttribute}...</p>
      ) : groupAttributeSummaryData ? (
        <ServiceAttributeSummary data={groupAttributeSummaryData} />
      ) : (
        !groupAttributeSummaryState.error && <p>No summary data available for attribute {selectedGroupAttribute}.</p>
      )}
    </div>
    <PulseContentFooter currentStartDate={currentStartDate} currentEndDate={currentEndDate} />
    </>
  );
};

export const getGroupAttributeRawDataPayload = (
  groupAttributeSummaryData: ServiceAttributeSummaryData,
  selectedGroupAttribute: string
): { data: any; message: string } | null => {
  if (!groupAttributeSummaryData) return null;
  return {
    data: groupAttributeSummaryData,
    message: `Summary data for attribute ${selectedGroupAttribute} copied to clipboard!`,
  };
};

export const getGroupAttributeViewCopyInfo = (selectedGroupAttribute: string) => {
  return {
    elementId: GROUP_ATTRIBUTE_VIEW_CONTENT_ID,
    successMessage: `View for attribute ${selectedGroupAttribute} summary copied!`,
  };
};

export const getGroupAttributePrintMetadata = (servicesGroup: string, selectedGroupAttribute: string) => {
  return {
    elementId: GROUP_ATTRIBUTE_VIEW_CONTENT_ID,
    jobTitle: `Summary for ${selectedGroupAttribute} in ${servicesGroup} - ${new Date().toLocaleDateString()}`,
    headerContent: `Attribute Summary: ${selectedGroupAttribute}${servicesGroup && servicesGroup !== "All" ? ` in ${servicesGroup}` : ''}`,
  };
};

export default GroupAttributeView;